import React, { useState, useEffect } from 'react';
import { Bell, Smartphone, CheckCircle2, Car, Key, Ticket, Trash2, Clock, RefreshCw, Check, X, Ban, Megaphone, BarChart3 } from 'lucide-react';
import { useFlashMessage } from '../components/FlashMessage';
import ConfirmDialog from '../components/ConfirmDialog';
import API from '../services/api';
import { FormSelect } from '../components/FormSelect';

const AdminNotifications = () => {
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notifToDelete, setNotifToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    status: 'all',
    search: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        type: filters.type,
        category: filters.category,
        status: filters.status,
        search: filters.search
      });

      const [notifsRes, statsRes] = await Promise.all([
        API.get(`/admin/notifications?${queryParams}`),
        API.get('/admin/notifications/stats')
      ]);

      if (notifsRes.data.success) {
        setNotifications(notifsRes.data.data);
        if (notifsRes.data.pagination) {
          setPagination(prev => ({
            ...prev,
            ...notifsRes.data.pagination
          }));
        }
      }

      if (statsRes.data.success) setStats(statsRes.data.data);

    } catch (error) {
      showDanger('Veriler yüklenirken hata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (notif) => {
    setNotifToDelete(notif);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await API.delete(`/admin/notifications/${notifToDelete.id}`);
      showSuccess('Bildirim başarıyla silindi');
      setDeleteDialogOpen(false);
      setNotifToDelete(null);
      loadData();
    } catch (error) {
      showDanger('Silme başarısız: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRetry = async (notifId) => {
    try {
      await API.post(`/admin/notifications/${notifId}/retry`);
      showSuccess('Bildirim yeniden deneme kuyruğuna eklendi');
      loadData();
    } catch (error) {
      showDanger('Yeniden deneme başarısız: ' + error.message);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', Icon: Clock, text: 'Beklemede' },
      processing: { color: 'bg-blue-100 text-blue-800', Icon: RefreshCw, text: 'İşleniyor' },
      sent: { color: 'bg-green-100 text-green-800', Icon: CheckCircle2, text: 'Gönderildi' },
      used: { color: 'bg-teal-100 text-teal-800', Icon: Check, text: 'Kullanıldı' },
      failed: { color: 'bg-red-100 text-red-800', Icon: X, text: 'Başarısız' },
      cancelled: { color: 'bg-gray-100 text-gray-800', Icon: Ban, text: 'İptal' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.Icon;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.color} flex items-center gap-1 w-fit`}>
        <IconComponent className="w-3 h-3" /> {config.text}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    return type === 'sms'
      ? <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 flex items-center gap-1 w-fit">
          <Smartphone className="w-3 h-3" /> SMS
        </span>
      : <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 flex items-center gap-1 w-fit">
          <Bell className="w-3 h-3" /> Push
        </span>;
  };

  const getCategoryBadge = (category) => {
    const categoryConfig = {
      vehicle_call: { color: 'bg-red-50 text-[#7a2c2c]', Icon: Car, text: 'Araç Çağrı' },
      password_reset: { color: 'bg-orange-100 text-orange-800', Icon: Key, text: 'Şifre Sıfırlama' },
      ticket_notification: { color: 'bg-green-100 text-green-800', Icon: Ticket, text: 'Bilet Bildirimi' },
      general: { color: 'bg-gray-100 text-gray-800', Icon: Megaphone, text: 'Genel' }
    };

    const config = categoryConfig[category] || categoryConfig.general;
    const IconComponent = config.Icon;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.color} flex items-center gap-1 w-fit`}>
        <IconComponent className="w-3 h-3" /> {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7a2c2c]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <FlashContainer />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bildirim Yönetimi</h1>
        <p className="text-gray-600 mt-1">Sistem bildirimleri ve SMS gönderimlerini takip edin</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 md:gap-3 lg:gap-4">
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <BarChart3 className="w-6 h-6 lg:w-8 lg:h-8 text-gray-700" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Toplam</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <Clock className="w-6 h-6 lg:w-8 lg:h-8 text-yellow-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-yellow-600">{stats.pending || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Beklemede</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <RefreshCw className="w-6 h-6 lg:w-8 lg:h-8 text-blue-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-blue-600">{stats.processing || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">İşleniyor</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <CheckCircle2 className="w-6 h-6 lg:w-8 lg:h-8 text-green-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.sent || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Gönderildi</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <X className="w-6 h-6 lg:w-8 lg:h-8 text-red-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-red-600">{stats.failed || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Başarısız</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <Smartphone className="w-6 h-6 lg:w-8 lg:h-8 text-purple-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-purple-600">{stats.sms_count || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">SMS</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <Bell className="w-6 h-6 lg:w-8 lg:h-8 text-blue-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-blue-600">{stats.push_count || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Push</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center">
                <Car className="w-6 h-6 lg:w-8 lg:h-8 text-[#7a2c2c]" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-[#7a2c2c]">{stats.vehicle_call_count || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Araç Çağrı</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tip</label>
              <FormSelect
                name="type"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'sms', label: 'SMS' },
                  { value: 'push_notification', label: 'Push Bildirim' }
                ]}
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
              <FormSelect
                name="category"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'vehicle_call', label: 'Araç Çağrı' },
                  { value: 'password_reset', label: 'Şifre Sıfırlama' },
                  { value: 'ticket_notification', label: 'Bilet Bildirimi' },
                  { value: 'general', label: 'Genel' }
                ]}
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
              <FormSelect
                name="status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'pending', label: 'Beklemede' },
                  { value: 'processing', label: 'İşleniyor' },
                  { value: 'sent', label: 'Gönderildi' },
                  { value: 'used', label: 'Kullanıldı' },
                  { value: 'failed', label: 'Başarısız' },
                  { value: 'cancelled', label: 'İptal' }
                ]}
              />
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ara</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Başlık, mesaj, telefon..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#7a2c2c] focus:ring-1 focus:ring-[#7a2c2c]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Bildirimler ({pagination.totalCount || 0})
          </h3>
        </div>
        <div className="card-body p-0">
          {notifications.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tip
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kategori
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Başlık
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Alıcı
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lokasyon
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Oluşturulma
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {notifications.map(notif => (
                      <tr key={notif.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getTypeBadge(notif.type)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getCategoryBadge(notif.category)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="max-w-xs">
                            <div className="font-medium text-gray-900">{notif.title || '-'}</div>
                            <div className="text-xs text-gray-500 truncate">{notif.message || '-'}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {notif.recipient_phone ? (
                            <span className="text-gray-900">{notif.recipient_phone}</span>
                          ) : notif.recipient_name ? (
                            <span className="text-gray-900">{notif.recipient_name}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {notif.location_name || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getStatusBadge(notif.status)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(notif.created_at)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {notif.status === 'failed' && (
                              <button
                                onClick={() => handleRetry(notif.id)}
                                className="text-[#7a2c2c] hover:text-[#5a1f1f]"
                                title="Yeniden Dene"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteClick(notif)}
                              className="text-red-600 hover:text-red-800"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3 p-4">
                {notifications.map(notif => (
                  <div key={notif.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2">
                      {getTypeBadge(notif.type)}
                      {getCategoryBadge(notif.category)}
                      {getStatusBadge(notif.status)}
                    </div>

                    {/* Title and Message */}
                    <div className="mb-3">
                      <div className="font-medium text-gray-900 mb-1">{notif.title || 'Başlıksız'}</div>
                      <div className="text-sm text-gray-600 line-clamp-2">{notif.message || '-'}</div>
                    </div>

                    {/* Info */}
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-600">Alıcı</div>
                        <div className="font-medium text-gray-900 truncate">
                          {notif.recipient_phone || notif.recipient_name || '-'}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-600">Lokasyon</div>
                        <div className="font-medium text-gray-900 truncate">{notif.location_name || '-'}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2 col-span-2">
                        <div className="text-gray-600">Oluşturulma</div>
                        <div className="font-medium text-gray-900">{formatDate(notif.created_at)}</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {notif.status === 'failed' && (
                        <button
                          onClick={() => handleRetry(notif.id)}
                          className="flex-1 py-2 px-3 bg-red-50 text-[#7a2c2c] rounded-lg text-sm font-medium hover:bg-red-100 flex items-center justify-center gap-1"
                        >
                          <RefreshCw className="w-4 h-4" /> Yeniden Dene
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteClick(notif)}
                        className="flex-1 py-2 px-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" /> Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Sayfa <span className="font-medium">{pagination.currentPage}</span> / <span className="font-medium">{pagination.totalPages}</span>
                      <span className="ml-2 text-gray-500">
                        (Toplam {pagination.totalCount} bildirim)
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                        disabled={!pagination.hasPrevPage}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← Önceki
                      </button>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                        disabled={!pagination.hasNextPage}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Sonraki →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mb-4 flex justify-center">
                <Bell className="w-16 h-16 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg">Bildirim bulunamadı</p>
              <p className="text-gray-400 text-sm">Filtreleri değiştirerek tekrar deneyin</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Bildirim Sil"
        message={`"${notifToDelete?.title || 'Bu bildirimi'}" kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminNotifications;
